import type { UserAddressCreatePayload } from "@/services/userAddressesService";

type LegacyAddressComponent = google.maps.GeocoderAddressComponent;
type ModernAddressComponent = google.maps.places.AddressComponent;

function findLegacyComponent(
  components: LegacyAddressComponent[],
  ...types: string[]
): LegacyAddressComponent | undefined {
  return components.find((c) => c.types.some((t) => types.includes(t)));
}

/** Legacy `Autocomplete` / PlacesService {@link google.maps.places.PlaceResult}. */
export function googlePlaceToAddressPayload(place: google.maps.places.PlaceResult): UserAddressCreatePayload {
  const comps = place.address_components ?? [];
  const streetNumber = findLegacyComponent(comps, "street_number")?.long_name;
  const route = findLegacyComponent(comps, "route")?.long_name;
  const neighborhood =
    findLegacyComponent(comps, "sublocality", "sublocality_level_1")?.long_name ??
    findLegacyComponent(comps, "neighborhood")?.long_name;
  const city =
    findLegacyComponent(comps, "locality")?.long_name ??
    findLegacyComponent(comps, "administrative_area_level_2")?.long_name;
  const adminArea = findLegacyComponent(comps, "administrative_area_level_1")?.long_name;
  const postalCode = findLegacyComponent(comps, "postal_code")?.long_name;
  const country = findLegacyComponent(comps, "country");
  const countryCode = country?.short_name?.toUpperCase() ?? "UY";
  const lat = place.geometry?.location?.lat();
  const lng = place.geometry?.location?.lng();
  const subpremiseL = findLegacyComponent(comps, "subpremise")?.long_name;
  const premiseL = findLegacyComponent(comps, "premise")?.long_name;
  const addressLine2 =
    [premiseL?.trim(), subpremiseL?.trim()].filter(Boolean).join(", ") || undefined;

  return {
    googlePlaceId: place.place_id ?? undefined,
    formattedAddress: place.formatted_address ?? undefined,
    latitude: lat !== undefined ? lat : undefined,
    longitude: lng !== undefined ? lng : undefined,
    route: route ?? undefined,
    streetNumber: streetNumber ?? undefined,
    addressLine2,
    neighborhood: neighborhood ?? undefined,
    city: city ?? undefined,
    adminArea: adminArea ?? undefined,
    postalCode: postalCode ?? undefined,
    countryCode,
  };
}

function longNameFromComponent(c: ModernAddressComponent | undefined): string | undefined {
  if (!c) return undefined;
  const anyc = c as ModernAddressComponent & { long_name?: string };
  return c.longText ?? anyc.long_name;
}

function shortCountryFromComponent(c: ModernAddressComponent | undefined): string | undefined {
  if (!c) return undefined;
  const anyc = c as ModernAddressComponent & { short_name?: string };
  return (c.shortText ?? anyc.short_name)?.toUpperCase();
}

function findModernComponent(
  components: ModernAddressComponent[] | undefined,
  ...types: string[]
): ModernAddressComponent | undefined {
  return components?.find((c) => c.types.some((t) => types.includes(t)));
}

function readLatLng(location: google.maps.LatLng | google.maps.LatLngLiteral | undefined | null): {
  lat?: number;
  lng?: number;
} {
  if (!location) return {};
  if (typeof (location as google.maps.LatLng).lat === "function") {
    const ll = location as google.maps.LatLng;
    return { lat: ll.lat(), lng: ll.lng() };
  }
  const lit = location as google.maps.LatLngLiteral;
  return { lat: lit.lat, lng: lit.lng };
}

/**
 * Build one-line formatted address: street + number first, then unit (apto / premise), then locality.
 * Google's `formattedAddress` often lists subpremise before the street in Uruguay.
 */
function buildOrderedFormattedAddress(
  place: google.maps.places.Place,
  comps: ModernAddressComponent[] | undefined,
  parts: {
    route?: string;
    streetNumber?: string;
    neighborhood?: string;
    city?: string;
    adminArea?: string;
    postalCode?: string;
  },
): string | undefined {
  if (!comps?.length) {
    return place.formattedAddress ?? undefined;
  }

  const subpremise = longNameFromComponent(findModernComponent(comps, "subpremise"));
  const premise = longNameFromComponent(findModernComponent(comps, "premise"));
  const countryName = longNameFromComponent(findModernComponent(comps, "country"));

  const streetLine = [parts.route, parts.streetNumber].filter(Boolean).join(" ").trim();
  const cityLine = [parts.postalCode, parts.city].filter(Boolean).join(" ").trim();
  const unitLine = [premise?.trim(), subpremise?.trim()].filter(Boolean).join(", ");

  const segments: string[] = [];
  if (streetLine) segments.push(streetLine);
  if (unitLine) segments.push(unitLine);
  if (parts.neighborhood?.trim()) segments.push(parts.neighborhood.trim());
  if (cityLine) segments.push(cityLine);
  if (parts.adminArea?.trim()) segments.push(parts.adminArea.trim());
  if (countryName?.trim()) segments.push(countryName.trim());

  if (segments.length > 0) return segments.join(", ");
  return place.formattedAddress ?? undefined;
}

/**
 * New Places API {@link google.maps.places.Place} after `fetchFields` (e.g. from
 * {@link google.maps.places.PlaceAutocompleteElement} `gmp-select`).
 */
export function googlePlaceClassToPayload(place: google.maps.places.Place): UserAddressCreatePayload {
  const comps = place.addressComponents;
  const streetNumber = longNameFromComponent(findModernComponent(comps, "street_number"));
  const route = longNameFromComponent(findModernComponent(comps, "route"));
  const neighborhood =
    longNameFromComponent(findModernComponent(comps, "sublocality", "sublocality_level_1")) ??
    longNameFromComponent(findModernComponent(comps, "neighborhood"));
  const city =
    longNameFromComponent(findModernComponent(comps, "locality")) ??
    longNameFromComponent(findModernComponent(comps, "administrative_area_level_2"));
  const adminArea = longNameFromComponent(findModernComponent(comps, "administrative_area_level_1"));
  const postalCode = longNameFromComponent(findModernComponent(comps, "postal_code"));
  const country = findModernComponent(comps, "country");
  const countryCode = shortCountryFromComponent(country) ?? "UY";
  const { lat, lng } = readLatLng(place.location ?? null);

  const formattedAddress = buildOrderedFormattedAddress(place, comps, {
    route,
    streetNumber,
    neighborhood,
    city,
    adminArea,
    postalCode,
  });

  const subpremise = longNameFromComponent(findModernComponent(comps, "subpremise"));
  const premise = longNameFromComponent(findModernComponent(comps, "premise"));
  const addressLine2 =
    [premise?.trim(), subpremise?.trim()].filter(Boolean).join(", ") || undefined;

  return {
    googlePlaceId: place.id ?? undefined,
    formattedAddress,
    latitude: lat,
    longitude: lng,
    route: route ?? undefined,
    streetNumber: streetNumber ?? undefined,
    addressLine2,
    neighborhood: neighborhood ?? undefined,
    city: city ?? undefined,
    adminArea: adminArea ?? undefined,
    postalCode: postalCode ?? undefined,
    countryCode,
  };
}
